import React, { useCallback } from 'react';

import { useLiveQuery } from 'dexie-react-hooks';
import { ChevronRight } from 'lucide-react';

import db from '../../lib/db';
import { displayWidgetSubjectLabel } from '../../lib/messagePreview';
import { cn } from '../../lib/utils';
import { SCROLL_TO_WIDGET_EVENT } from '../../lib/widgetEvents';
import { markWidgetViewed } from '../../lib/widgetViewedStore';

import { useOptionalRouteWorkspace } from '../../contexts/RouteWorkspaceContext';
import { useWidgetViewedAt } from '../../hooks/useWidgetViewedAt';
import { ProfileEntity } from '../ProfileEntity';
import Flexicon from '../shared/Flexicon';
import WidgetIcon from '../shared/WidgetIcon';
import LocalTime from './LocalTime';

const safeExternalUrl = (value) => {
  if (typeof value !== 'string') return null;

  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : null;
  } catch {
    return null;
  }
};

// The chip renders "unseen" while the announcement is newer than the viewer's
// last look at the source widget, then decays to "seen" once they've viewed it
// — via this chip, the context menu, or scrolling the widget into view on the
// pane. The viewer's own announcements never render loud.
// Figma: unseen (7715:119970) is a raised white pill — soft 0/4/10 drop
// shadow, no border — with an 8px badge dot over the avatar's top-left corner.
// Seen (7715:119936) decays to a flat gray pill with a hairline, no dot, no
// shadow. Both keep a transparent-vs-real border of the same width so the
// state flip never shifts layout.
const chipStateClassNames = {
  unseen:
    'border-transparent bg-white shadow-[0px_4px_10px_0px_rgba(0,0,0,0.1)] hover:bg-gray-50 dark:border-neutral-700 dark:bg-neutral-900 dark:hover:bg-neutral-800',
  seen: 'border-gray-200 bg-gray-200 hover:bg-gray-300/60 dark:border-white/10 dark:bg-neutral-800/80 dark:hover:bg-neutral-800',
};

// The chevron stays at the same quiet gray in either state.
const glyphClassName = 'text-gray-400 dark:text-neutral-500';

// Per-segment text colors carry the reading hierarchy (name strongest, subject
// next, verb quietest) and the read/unread split: unread runs at full
// strength, read fades the sentence. Link subjects stay gray with an underline
// at rest and only go sky on hover.
const segmentClassNames = {
  unseen: {
    name: 'text-gray-900 dark:text-neutral-100',
    verb: 'text-gray-500 dark:text-neutral-400',
    subject: 'text-gray-700 dark:text-neutral-200',
    subjectLink: 'text-gray-700 underline hover:text-sky-600 dark:text-neutral-200 dark:hover:text-sky-400',
  },
  seen: {
    name: 'text-gray-500 dark:text-neutral-400',
    verb: 'text-gray-400 dark:text-neutral-500',
    subject: 'text-gray-500 dark:text-neutral-400',
    subjectLink: 'text-gray-500 underline hover:text-sky-600 dark:text-neutral-400 dark:hover:text-sky-400',
  },
};

// Figma geometry: 6px pill padding on the avatar side, 16px on the text side,
// 10px avatar↔text gap, 4px gaps within the sentence, 13px/20px type.
// whitespace-nowrap + per-segment shrink-0 (only the subject truncates) keeps
// the chip strictly one line.
const chipBaseClassName =
  'inline-flex max-w-full min-w-0 cursor-pointer items-center gap-[10px] rounded-full border-[0.5px] py-1.5 pr-4 pl-1.5 text-left text-cursed leading-5 font-normal whitespace-nowrap';

// One chip serves both widget events: activity a widget reports about itself
// ("Ada added a task") and the widget's own arrival ("Ada added Gradient"). Both
// jump to the widget on the pane, so anatomy, unseen dot, and read decay are
// shared — only the sentence's subject differs.
const WidgetEventChip = ({ announcement, actor, verb, subjectLabel, subjectUrl, sourceWidget, sourceWidgetId }) => {
  const timestamp = Number(announcement?.timestamp);

  const routeWorkspace = useOptionalRouteWorkspace();
  const viewedAt = useWidgetViewedAt(sourceWidgetId);
  const isOwnAnnouncement = Boolean(actor?.id && routeWorkspace?.profileId && String(actor.id) === String(routeWorkspace.profileId));
  const isUnseen = !isOwnAnnouncement && Boolean(sourceWidgetId) && (viewedAt === null || (Number.isFinite(timestamp) && timestamp > viewedAt));

  // The stable widget-announcement-* classes exist for BG-image mode: read
  // chips restyle as glass over workspace imagery (application.css), while
  // unread keeps the raised white pill everywhere.
  const chipClassName = cn(
    !isUnseen && 'widget-announcement-chip--seen',
    chipBaseClassName,
    chipStateClassNames[isUnseen ? 'unseen' : 'seen'],
  );
  const segments = segmentClassNames[isUnseen ? 'unseen' : 'seen'];

  const handleChipClick = useCallback(() => {
    if (!sourceWidgetId) return;

    markWidgetViewed(sourceWidgetId);
    window.dispatchEvent(
      new CustomEvent(SCROLL_TO_WIDGET_EVENT, {
        detail: {
          widgetId: sourceWidgetId,
          messageId: announcement?.id,
        },
      }),
    );
  }, [announcement?.id, sourceWidgetId]);

  // The chip itself always jumps to the widget; the underlined subject opens
  // the URL like a link in a message bubble, without also triggering the jump.
  const handleSubjectLinkClick = useCallback(
    (event) => {
      event.stopPropagation();
      if (sourceWidgetId) markWidgetViewed(sourceWidgetId);
    },
    [sourceWidgetId],
  );

  const handleChipKeyDown = useCallback(
    (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      handleChipClick();
    },
    [handleChipClick],
  );

  // Chip anatomy: [avatar] then the sentence group: [name] [verb] [widget icon] [subject words] [affordance]
  const chipContents = (
    <>
      {actor ? (
        <span className="inline-flex size-[25px] shrink-0">
          <Flexicon resource={actor} size="size-[25px]" />
        </span>
      ) : null}
      <span className="flex min-w-0 items-center gap-1">
        <span className={cn('widget-announcement-name shrink-0 whitespace-nowrap', segments.name)}>
          {actor ? <ProfileEntity profile={actor} showAvatar={false} /> : <span className="font-semibold">Someone</span>}
        </span>
        <span className={cn('widget-announcement-verb shrink-0', segments.verb)}>{verb}</span>
        <span className="inline-flex size-4 shrink-0">
          <WidgetIcon widget={sourceWidget} className="size-4" />
        </span>
        {subjectUrl ? (
          <a
            href={subjectUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-app-region="none"
            onClick={handleSubjectLinkClick}
            className={cn('widget-announcement-subject-link min-w-0 cursor-pointer truncate font-medium', segments.subjectLink)}
          >
            {subjectLabel}
          </a>
        ) : (
          <span className={cn('widget-announcement-subject min-w-0 truncate font-medium', segments.subject)}>{subjectLabel}</span>
        )}
        <ChevronRight size={12} className={cn('widget-announcement-glyph shrink-0', glyphClassName)} />
      </span>
    </>
  );

  return (
    <div data-debug-id={announcement?.id} data-debug-sort-order={announcement?.sort_order} className="px-2 py-4 text-right">
      <LocalTime
        timestamp={announcement?.timestamp}
        format="%-I:%M %p"
        className="widget-announcement-timestamp mb-1.5 block text-[11px] leading-5 font-normal lining-nums proportional-nums select-none"
      />
      <span className="relative inline-flex max-w-full items-center">
        {/* Figma 7715:119975: 8px badge dot at (6,6), riding the avatar's
            top-left corner — backed by the pill surface (readable over BG
            imagery) and out of the content flow, so nothing shifts on read. */}
        {isUnseen ? <span aria-hidden className="widget-announcement-dot absolute top-[6px] left-[6px] z-10 size-2 rounded-full" /> : null}
        {subjectUrl ? (
          // Not a <button>: the underlined subject <a> nests inside, and anchors
          // inside buttons are invalid HTML with flaky click behavior.
          <span role="button" tabIndex={0} onClick={handleChipClick} onKeyDown={handleChipKeyDown} data-app-region="none" className={chipClassName}>
            {chipContents}
          </span>
        ) : (
          <button type="button" onClick={handleChipClick} data-app-region="none" className={chipClassName}>
            {chipContents}
          </button>
        )}
      </span>
    </div>
  );
};

const useAnnouncementWidget = (announcement, widgetId) => {
  const workspaceId = useOptionalRouteWorkspace()?.workspaceId || null;
  const live = useLiveQuery(
    async function loadAnnouncementWidget() {
      if (!workspaceId || !widgetId) return null;

      return db.widgets.get([workspaceId, widgetId]);
    },
    [widgetId, workspaceId],
  );

  return live ?? announcement?.source_widget ?? null;
};

const WidgetAnnouncement = ({ announcement }) => {
  const data = announcement?.data || {};
  const subject = data.subject || {};
  const sourceWidgetId = announcement?.source_widget_id || announcement?.source_widget?.id;
  const sourceWidget = useAnnouncementWidget(announcement, sourceWidgetId);

  return (
    <WidgetEventChip
      announcement={announcement}
      actor={(announcement?.actors || [])[0]}
      verb={data.verb || 'updated'}
      subjectLabel={displayWidgetSubjectLabel(subject)}
      subjectUrl={subject.kind === 'link' ? safeExternalUrl(subject.payload?.url) : null}
      sourceWidget={sourceWidget}
      sourceWidgetId={sourceWidgetId}
    />
  );
};

// Announcements carry no hydrated actors — just the profile hashid — so the
// avatar and name come from Dexie, seeded by the row's entity snapshots so a
// cold query never flashes the "Someone" fallback.
const useAnnouncementActor = (announcement, profileId) => {
  const workspaceId = useOptionalRouteWorkspace()?.workspaceId || null;
  const live = useLiveQuery(() => {
    if (!profileId || !workspaceId) return null;
    return db.profiles.get([workspaceId, profileId]);
  }, [profileId, workspaceId]);

  return live ?? (profileId ? announcement?.entity_snapshots?.profile?.[profileId] : null) ?? null;
};

// A widget arriving in the chat is a widget event like any other, so it wears
// the same chip rather than the plain announcement row. The widget is its own
// subject here, which is why the verb is fixed and there is no link to open.
export const WidgetPublishedAnnouncement = ({ announcement }) => {
  const data = announcement?.data || {};
  const actor = useAnnouncementActor(announcement, data.profile_id);
  const sourceWidget = useAnnouncementWidget(announcement, data.widget_id);

  return (
    <WidgetEventChip
      announcement={announcement}
      actor={actor}
      verb={data.widget_action === 'updated' ? 'updated' : 'added'}
      subjectLabel={data.widget_title || 'a widget'}
      subjectUrl={null}
      sourceWidget={sourceWidget}
      sourceWidgetId={data.widget_id || null}
    />
  );
};

export default WidgetAnnouncement;
